import React, { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Code2, Copy, Check, ExternalLink } from "lucide-react";
import { encodeShare } from "../lib/share";
import { toast } from "sonner";
import { useI18n } from "../lib/i18n";

export default function EmbedDialog({ currentState }) {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [height, setHeight] = useState(680);
  const [showChart, setShowChart] = useState(true);
  const [showTable, setShowTable] = useState(true);
  const [theme, setTheme] = useState("dark");

  const embedUrl = useMemo(() => {
    const token = encodeShare(currentState);
    const qs = new URLSearchParams();
    qs.set("s", token);
    if (!showChart) qs.set("chart", "0");
    if (!showTable) qs.set("table", "0");
    qs.set("theme", theme);
    return `${window.location.origin}/embed?${qs.toString()}`;
  }, [currentState, showChart, showTable, theme]);

  const snippet = `<iframe
  src="${embedUrl}"
  width="100%"
  height="${height}"
  frameborder="0"
  style="border:0;border-radius:16px;max-width:960px;"
  title="WordCripto — Simulatore 10 anni"
  loading="lazy"
></iframe>`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      toast.success(lang === "it" ? "Snippet copiato" : "Snippet copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt("Copy", snippet);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="wc-btn-outline" data-testid="embed-btn">
          <Code2 size={14} /> {lang === "it" ? "Widget" : "Embed"}
        </Button>
      </DialogTrigger>
      <DialogContent className="wc-embed-dialog" data-testid="embed-dialog">
        <DialogHeader>
          <DialogTitle>
            {lang === "it" ? "Incorpora sul tuo sito" : "Embed on your site"}
          </DialogTitle>
        </DialogHeader>

        <p className="wc-embed-dialog-lead">
          {lang === "it"
            ? "Copia questo snippet e incollalo dove vuoi mostrare il simulatore (es. wordcripto.com). Include automaticamente il pulsante di iscrizione con referral."
            : "Copy this snippet and paste it wherever you want to show the simulator (e.g. wordcripto.com). It includes the referral signup button."}
        </p>

        <div className="wc-embed-options">
          <label className="wc-embed-opt">
            <input
              type="checkbox"
              checked={showChart}
              onChange={(e) => setShowChart(e.target.checked)}
              data-testid="embed-opt-chart"
            />
            <span>{lang === "it" ? "Grafico" : "Chart"}</span>
          </label>
          <label className="wc-embed-opt">
            <input
              type="checkbox"
              checked={showTable}
              onChange={(e) => setShowTable(e.target.checked)}
              data-testid="embed-opt-table"
            />
            <span>{lang === "it" ? "Tabella annuale" : "Yearly table"}</span>
          </label>
          <label className="wc-embed-opt">
            <span>{lang === "it" ? "Tema" : "Theme"}:</span>
            <select
              value={theme}
              onChange={(e) => setTheme(e.target.value)}
              data-testid="embed-opt-theme"
              className="wc-embed-opt-select"
            >
              <option value="dark">Dark</option>
              <option value="light">Light</option>
            </select>
          </label>
          <label className="wc-embed-opt">
            <span>{lang === "it" ? "Altezza" : "Height"}:</span>
            <input
              type="number"
              min="300"
              step="20"
              value={height}
              onChange={(e) => setHeight(Number(e.target.value) || 680)}
              className="wc-embed-opt-num"
              data-testid="embed-opt-height"
            />
            px
          </label>
        </div>

        <Textarea
          value={snippet}
          readOnly
          className="wc-embed-snippet"
          data-testid="embed-snippet"
        />

        <div className="wc-embed-actions">
          <Button onClick={copy} className="wc-btn-primary" data-testid="embed-copy">
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied
              ? lang === "it" ? "Copiato" : "Copied"
              : lang === "it" ? "Copia snippet" : "Copy snippet"}
          </Button>
          <a
            href={embedUrl}
            target="_blank"
            rel="noreferrer"
            className="wc-btn-outline wc-btn-linklike"
            data-testid="embed-preview"
          >
            <ExternalLink size={14} /> {lang === "it" ? "Anteprima" : "Preview"}
          </a>
        </div>

        <div className="wc-embed-preview-wrap">
          <span className="wc-embed-preview-label">
            {lang === "it" ? "Anteprima live" : "Live preview"}
          </span>
          <iframe
            src={embedUrl}
            width="100%"
            height={Math.min(height, 460)}
            frameBorder="0"
            title="preview"
            style={{ border: 0, borderRadius: 12 }}
            data-testid="embed-preview-iframe"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
